import { Component, computed, effect, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs';

import { environment } from '../../../../environments/environment.development';
import { ApplicationState } from '../../state/application-state.service';
import { ConfirmDialog } from '../../ui/confirm-dialog/confirm-dialog.component';
import { NavItemComponent } from '../nav-item/nav-item.component';
import { UserProfileNavItemComponent } from '../user-profile-nav-item/user-profile-nav-item.component';
import { ReportsIconListComponent } from '../../../features/insights/feature/reports-icon-list/reports-icon-list.component';

@Component({
  selector: 'app-sidenav',
  imports: [
    RouterModule,
    NavItemComponent,
    UserProfileNavItemComponent,
    ConfirmDialog,
    ReportsIconListComponent,
  ],
  templateUrl: './sidenav.component.html',
})
export class SidenavComponent {
  private readonly authService = inject(MsalService);
  private readonly router = inject(Router);
  private readonly applicationState = inject(ApplicationState);

  readonly route = signal(this.router.url);
  readonly screenWidth = signal(window.innerWidth);
  readonly confirmDialogOpen = signal(false);
  readonly showMobileMenu = signal(false);

  readonly collapsedChange = output<boolean>();

  readonly showHeader = computed(() => this.screenWidth() <= 768);
  readonly isReportRoute = computed(() => this.route().startsWith('/report'));

  readonly collapsed = computed(() => {
    const route = this.route();

    return [
      '/report',
      '/bluecreek',
      '/service-tooling',
      '/admin',
      '/health-guard',
    ].some(prefix => route.startsWith(prefix)) || route === '/login';
  });

  readonly authenticated = this.applicationState.isAuthenticated;
  readonly activeAccount = this.applicationState.activeAccount;
  readonly isOwner = this.applicationState.isOwner;
  readonly isContributor = this.applicationState.isContributor;
  readonly isReader = this.applicationState.isReader;
  readonly isHealthGuardUser = this.applicationState.isHealthGuardUser;

  constructor() {
    const onResize = () => this.screenWidth.set(window.innerWidth);
    window.addEventListener('resize', onResize);

    effect(onCleanup => {
      onCleanup(() => window.removeEventListener('resize', onResize));
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(event => this.route.set(event.urlAfterRedirects));

    effect(() => this.collapsedChange.emit(this.collapsed()));

    effect(() => {
      if (this.showHeader() && this.route()) {
        this.showMobileMenu.set(false);
      }
    });
  }

  switchAccount() {
    this.authService.loginPopup({
      authority: environment.msalConfig.auth.authority,
      prompt: 'select_account',
      scopes: [...environment.apiConfig.scopes, 'user.read'],
    }).subscribe({
      next: response => this.authService.instance.setActiveAccount(response.account),
      error: error => console.warn('error switching accounts', error),
    });
  }

  logout() {
    this.confirmDialogOpen.set(false);
    this.authService.logoutRedirect();
  }
}